import * as React from 'react'
import { useState } from 'react'
import {
  Button,
  Alert,
  AlertVariant,
  Tooltip,
} from '@patternfly/react-core';
import { InfoCircleIcon } from '@patternfly/react-icons';
import { useMsal } from '@azure/msal-react';
// Import new consolidated hooks and utilities
import { useFormState } from '../hooks/useFormState';
import { useFormValidation } from '../hooks/useFormValidation';
import { useFormOptions } from '../hooks/useFormOptions';
import { createFormData, isDBRequiredForTechStack } from '../utils/formHelpers';
import { AdvancedOptionsData, InfraDependenciesData } from '../types/formTypes';

// Import generic/reusable components (keeping these)
import { BaseInput } from '../components/BaseInput';
import { Select } from '../components/Select';
import { MaintainerTeamsInput, useMaintainerTeamsInput } from '../components/MaintainerTeamsInput';
import { ServiceNameInput, useServiceNameInput } from '../components/ServiceNameInput';
import { AdvancedOptionsSection, useAdvancedOptionsValidation } from '../components/AdvancedOptionsSection';
import { InfraDependenciesSection, useInfraDependenciesValidation } from '../components/InfraDependenciesSection';

// Import React Query hooks
import { useAdvancedUser } from '../hooks/useAdvancedUser';
import { useGenerateMicroservice } from '../hooks/useGenerateMicroservice';
import { useClientEvent } from '@app/hooks/useClientEvent';

// Local UI state for section-level form data
const useApplicationState = () => {
  const [isAdvancedOptionsValid, setIsAdvancedOptionsValid] = useState<boolean>(true);
  const [advancedOptionsData, setAdvancedOptionsData] = useState<AdvancedOptionsData>({});
  const [isInfraDependenciesValid, setIsInfraDependenciesValid] = useState<boolean>(true);
  const [infraDependenciesData, setInfraDependenciesData] = useState<InfraDependenciesData>({});

  return {
    isAdvancedOptionsValid,
    setIsAdvancedOptionsValid,
    advancedOptionsData,
    setAdvancedOptionsData,
    isInfraDependenciesValid,
    setIsInfraDependenciesValid,
    infraDependenciesData,
    setInfraDependenciesData,
  };
};

const MicroserviceGenerator: React.FunctionComponent = () => {
  // React Query hooks (replace SDK + manual state)
  const { isAdvancedUser } = useAdvancedUser();
  const generateMutation = useGenerateMicroservice();
  const fireEvent = useClientEvent();

  // Local UI state for section-level form data
  const {
    isAdvancedOptionsValid,
    setIsAdvancedOptionsValid,
    advancedOptionsData,
    setAdvancedOptionsData,
    isInfraDependenciesValid,
    setIsInfraDependenciesValid,
    infraDependenciesData,
    setInfraDependenciesData,
  } = useApplicationState();

  // Form state using consolidated hook
  const formState = useFormState();
  const { accounts } = useMsal();
  const generatedBy = accounts[0]?.name || '';
  // Form options
  const { orgNameOptions, techStackOptions, databaseOptions, domainOptions, functionalAreaOptions, isLoading: optionsLoading, error: optionsError } = useFormOptions();

  // Maintainer teams with async validation (keeping this separate as it has complex logic)
  const {
    maintainerTeams: maintainerTeamsWithValidation,
    maintainerTeamsError: maintainerTeamsErrorWithValidation,
    isValidating: isMaintainerTeamsValidating,
    handleMaintainerTeamsChange: handleMaintainerTeamsChangeWithValidation,
    handleMaintainerTeamsBlur: handleMaintainerTeamsBlurWithValidation,
    validateMaintainerTeams: validateMaintainerTeamsWithValidation,
    isValid: isMaintainerTeamsValidWithValidation,
    isVerified: isMaintainerTeamsVerified
  } = useMaintainerTeamsInput(formState.orgName.value);

  // Service name with async validation using AsyncInput pattern
  const {
    serviceName: serviceNameWithValidation,
    serviceNameError: serviceNameErrorWithValidation,
    isValidating: isServiceNameValidating,
    isValid: isServiceNameValidWithValidation,
    isDependenciesMet: isServiceNameDependenciesMet,
    handleServiceNameChange: handleServiceNameChangeWithValidation,
    handleServiceNameBlur: handleServiceNameBlurWithValidation,
    validateServiceName: validateServiceNameWithValidation
  } = useServiceNameInput(
    formState.serviceName.value,
    formState.orgName.value,
    formState.techStack.value,
    formState.functionalArea.value,
    formState.domain.value,
  );

  // Section validation hooks
  const {
    infraDependenciesRef,
    validateInfraDependencies,
    resetInfraDependencies
  } = useInfraDependenciesValidation();

  const {
    advancedOptionsRef,
    validateAdvancedOptions,
    resetAdvancedOptions
  } = useAdvancedOptionsValidation();

  // Form validation
  const { isFormValid, validateAllFields } = useFormValidation({
    techStack: formState.techStack.value,
    serviceName: serviceNameWithValidation,
    orgName: formState.orgName.value,
    domain: formState.domain.value,
    generatedBy,
    maintainerTeams: maintainerTeamsWithValidation,
    infraDependenciesData,
    description: formState.description.value,
    isDescriptionValid: formState.description.isValid,
    isAdvancedUser,
    isTechStackValid: formState.techStack.isValid,
    isServiceNameValid: isServiceNameValidWithValidation,
    isOrgNameValid: formState.orgName.isValid,
    isDomainValid: formState.domain.isValid,
    isFunctionalAreaValid: formState.domain.isValid,
    isMaintainerTeamsValid: isMaintainerTeamsValidWithValidation,
    isInfraDependenciesValid,
    isAdvancedOptionsValid
  });

  // Derive mutation state for templates
  const isGenerating = generateMutation.isPending;
  const jobURL = generateMutation.data ?? '';
  const generationError = generateMutation.error ? (generateMutation.error as Error).message : '';

  // Form submission
  const handleSubmit = async () => {
    // Run synchronous validations
    validateAllFields(
      formState.techStack.validate,
      formState.serviceName.validate, // Keep the old validation for immediate feedback
      formState.orgName.validate,
      formState.description.validate,
      formState.domain.validate,
      formState.functionalArea.validate,
      validateMaintainerTeamsWithValidation
    );

    // Run async service name validation
    await validateServiceNameWithValidation();

    if (isFormValid()) {
      const formData = createFormData(
        formState.techStack.value,
        serviceNameWithValidation,
        formState.description.value,
        formState.domain.value,
        formState.functionalArea.value,
        formState.orgName.value,
        maintainerTeamsWithValidation,
        infraDependenciesData,
        advancedOptionsData,
        generatedBy,
      );

      generateMutation.mutate(formData);
      fireEvent('MICROSERVICE_GENERATE_CLICKED');
    }
  };

  // Render functions
  const renderAlerts = () => (
    <>
      {jobURL && (
        <Alert
          variant={AlertVariant.success}
          title="Microservice generation initiated!"
          style={{ marginBottom: '1rem' }}
          actionClose={
            <Button
              variant="plain"
              onClick={() => {
                generateMutation.reset();
                formState.resetForm();
                resetInfraDependencies();
                resetAdvancedOptions();
              }}
              aria-label="Close success alert"
            >
              Dismiss
            </Button>
          }
        >
          You can track the progress of your microservice generation at:{' '}
          <a href={jobURL} target="_blank" rel="noopener noreferrer">
            {jobURL}
          </a>
        </Alert>
      )}

      {generationError && (
        <Alert
          variant={AlertVariant.danger}
          title="Error generating microservice"
          style={{ marginBottom: '1rem' }}
          actionClose={
            <Button
              variant="plain"
              onClick={() => {
                generateMutation.reset();
                formState.resetForm();
                resetInfraDependencies();
                resetAdvancedOptions();
              }}
              aria-label="Close error alert"
            >
              Dismiss
            </Button>
          }
        >
          {generationError}
        </Alert>
      )}
    </>
  );

  return (
    <>
      <div className="page-header" style={{ marginBottom: '24px' }}>
        <h1 className="dx-title" style={{ fontSize: '24px' }}>⚡ Generate Microservice</h1>
        <p className="dx-text--secondary" style={{ marginTop: '4px' }}>
          Create a new microservice with all the boilerplate and CI/CD setup.{' '}
          <a target='_blank' rel='noopener noreferrer' href='https://euewizard.docs.teladoc.dev/#/pages/reusables/quick_start/general-guide'>Documentation</a>
        </p>
      </div>

      {renderAlerts()}

      {optionsLoading && (
        <Alert variant="info" title="Loading form options..." isInline style={{ marginBottom: '1rem' }}>
          Please wait while we load the form dropdown options.
        </Alert>
      )}
      {optionsError && (
        <Alert variant="warning" title="Failed to load some options" isInline style={{ marginBottom: '1rem' }}>
          {optionsError}. Using fallback options.
        </Alert>
      )}

      <div className="generator-container">
        <div className="generator-layout">
          {/* Left Column — Service Configuration */}
          <div className="generator-main">
            <div className="generator-form">
              <div className="form-section">
                <h3 className="form-section-title">📋 Service Configuration</h3>

                <div className="form-group">
                  <label className="form-label">Organization</label>
                  <Select
                    key={`org-name-${formState.orgName.value}`}
                    options={orgNameOptions}
                    initialValue={formState.orgName.value}
                    onSelect={formState.orgName.handleChange}
                    validated={formState.orgName.error ? 'error' : 'default'}
                    required={true}
                    placeholder="Select organization"
                  />
                  {formState.orgName.error && (
                    <div className="dx-helper-text dx-helper-text--error">{formState.orgName.error}</div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Tech Stack</label>
                  <Select
                    key={`tech-stack-${formState.techStack.value}`}
                    options={techStackOptions}
                    initialValue={formState.techStack.value}
                    onSelect={formState.techStack.handleChange}
                    validated={formState.techStack.error ? 'error' : 'default'}
                    required={true}
                    placeholder="Select technology stack"
                  />
                  {formState.techStack.error && (
                    <div className="dx-helper-text dx-helper-text--error">{formState.techStack.error}</div>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      Functional Area{' '}
                      <Tooltip content="Determines CI Job location inside CloudBees">
                        <InfoCircleIcon style={{ color: 'var(--text-muted)', cursor: 'pointer', verticalAlign: 'baseline', transform: 'translateY(1px)' }} />
                      </Tooltip>
                    </label>
                    <Select
                      key={`functionalArea-${formState.functionalArea.value}`}
                      options={functionalAreaOptions}
                      initialValue={formState.functionalArea.value}
                      onSelect={formState.functionalArea.handleChange}
                      validated={formState.functionalArea.error ? 'error' : 'default'}
                      required={true}
                      placeholder="Select functional area"
                    />
                    {formState.functionalArea.error && (
                      <div className="dx-helper-text dx-helper-text--error">{formState.functionalArea.error}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Domain</label>
                    <Select
                      key={`domain-${formState.domain.value}`}
                      options={domainOptions}
                      initialValue={formState.domain.value}
                      onSelect={formState.domain.handleChange}
                      validated={formState.domain.error ? 'error' : 'default'}
                      required={true}
                      placeholder="Select domain"
                    />
                    {formState.domain.error && (
                      <div className="dx-helper-text dx-helper-text--error">{formState.domain.error}</div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Service Name</label>
                  <ServiceNameInput
                    value={serviceNameWithValidation}
                    onChange={handleServiceNameChangeWithValidation}
                    onBlur={handleServiceNameBlurWithValidation}
                    isRequired={true}
                    error={serviceNameErrorWithValidation}
                    isValidating={isServiceNameValidating}
                    organization={formState.orgName.value}
                    techStack={formState.techStack.value}
                    functionalArea={formState.functionalArea.value}
                    domain={formState.domain.value}
                    disabled={!isServiceNameDependenciesMet}
                  />
                  <span className="form-helper">Use lowercase with hyphens. Will be prefixed with org name.</span>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Maintainer Teams{' '}
                    <Tooltip content="GitHub teams to give maintainer access to repository">
                      <InfoCircleIcon style={{ color: 'var(--text-muted)', cursor: 'pointer', verticalAlign: 'baseline', transform: 'translateY(3px)' }} />
                    </Tooltip>
                  </label>
                  <MaintainerTeamsInput
                    value={maintainerTeamsWithValidation}
                    onChange={handleMaintainerTeamsChangeWithValidation}
                    onBlur={handleMaintainerTeamsBlurWithValidation}
                    isRequired={true}
                    error={maintainerTeamsErrorWithValidation}
                    isValidating={isMaintainerTeamsValidating}
                    disabled={
                      !serviceNameWithValidation.trim() ||
                      !!serviceNameErrorWithValidation
                    }
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Description</label>
                  <BaseInput
                    value={formState.description.value}
                    onChange={formState.description.handleChange}
                    onBlur={formState.description.handleBlur}
                    isRequired={true}
                    type="textarea"
                    rows={4}
                    ariaLabel="Description Input"
                    placeholder={
                      !isMaintainerTeamsVerified || isMaintainerTeamsValidating
                        ? 'Maintainer Teams is required'
                        : 'Enter a description for the microservice'
                    }
                    error={formState.description.error}
                    disabled={!isMaintainerTeamsVerified || isMaintainerTeamsValidating}
                  />
                </div>
              </div>

              {/* Generate Button */}
              <div className="form-actions">
                <button
                  className="generate-btn"
                  disabled={!isFormValid() || isGenerating}
                  onClick={handleSubmit}
                  type="button"
                >
                  {isGenerating ? 'Generating...' : '🚀 Generate Microservice'}
                </button>
              </div>
            </div>
          </div>

          {/* Right Column — Infrastructure & Options */}
          <div className="generator-sidebar">
            {/* Infrastructure Dependencies */}
            {isDBRequiredForTechStack(formState.techStack.value) && (
              <InfraDependenciesSection
                ref={infraDependenciesRef}
                formRowStyle={{}}
                onValidationChange={setIsInfraDependenciesValid}
                onDataChange={setInfraDependenciesData}
                databaseOptions={databaseOptions}
                layout="card"
              />
            )}

            {/* Advanced Options */}
            {isAdvancedUser && (
              <AdvancedOptionsSection
                ref={advancedOptionsRef}
                formRowStyle={{}}
                onValidationChange={setIsAdvancedOptionsValid}
                onDataChange={setAdvancedOptionsData}
                layout="card"
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export { MicroserviceGenerator };